import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import { Plus, UserCircle, Search } from 'lucide-react';
import Navigation from '../components/Navigation';
import { toast } from '../lib/toast';
import EmployeeForm from '../components/EmployeeForm';
import ResourceFinder from '../components/ResourceFinder';

interface TeamMember {
  id: string;
  admin_id: string;
  name: string;
  email: string;
  job_title: string;
  technology: string[];
  availability: number;
  photo_url: string | null;
  created_at: string;
}

export default function Employees({ navigate }: { navigate: (to: string) => void }) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [showResourceFinder, setShowResourceFinder] = useState(false);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('admin_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTeamMembers(data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
      setTeamMembers(teamMembers.filter((member) => member.id !== memberId));
      toast.success('Team member removed successfully');
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Members</h1>
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
              <button
                onClick={() => setShowResourceFinder(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Search className="h-5 w-5 mr-2" />
                Find Resource
              </button>
              <button
                onClick={() => {
                  setEditingMember(null);
                  setShowForm(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Member
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner">Loading...</div>
            </div>
          ) : showForm ? (
            <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
              <EmployeeForm
                onSuccess={() => {
                  setShowForm(false);
                  fetchTeamMembers();
                }}
                onCancel={() => {
                  setShowForm(false);
                  setEditingMember(null);
                }}
                initialData={editingMember || undefined}
              />
            </div>
          ) : (
            <>
              {showResourceFinder && (
                <ResourceFinder
                  teamMembers={teamMembers}
                  onClose={() => setShowResourceFinder(false)}
                />
              )}
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        {member.photo_url ? (
                          <img
                            src={member.photo_url}
                            alt={member.name}
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          <UserCircle className="h-12 w-12 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {member.name}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {member.email}
                        </p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {member.job_title}
                        </p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Availability: {member.availability || 40} hours/week
                        </p>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {Array.isArray(member.technology) 
                            ? member.technology.map(tech => tech.replace(/[\[\]"]/g, '')).join(', ')
                            : typeof member.technology === 'string'
                              ? member.technology.replace(/[\[\]"]/g, '')
                              : ''}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          setEditingMember(member);
                          setShowForm(true);
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(member.id)}
                        className="text-sm text-red-600 hover:text-red-500 dark:text-red-400"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {teamMembers.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">
                      No team members found. Add one to get started!
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}